import React, { useState } from "react"
import styled from "styled-components"
import InfluencerCard from "./InfluencerCard"
import { Influencer } from "../../types/influencer"
import Button from "../common/Button"
import { FilterValues } from "../filters/FilterSection"

interface InfluencerListProps {
  influencers: Influencer[]
  isLoading: boolean
  hasSubmittedEmail: boolean
  onEmailSubmit?: (email: string) => void
  currentFilters: FilterValues
  onChangeFilters: () => void
  signupUrl?: string
  onSelectionChange?: (hasSelected: boolean) => void
  showCTA?: boolean
}

const ListContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[4]} 0;
`

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  }
`

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  text-align: left;
  max-width: none;
  margin-left: 0;
  margin-right: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: ${({ theme }) => theme.spacing[8]};
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  position: relative;
`

const InfluencerRow = styled.div<{ isPreview: boolean }>`
  position: relative;
  ${(props) => (props.isPreview ? "filter: blur(2px);" : "")}
  transition: filter 0.3s ease;
`

const ListHeader = styled.div`
  display: none;

  .header-col-influencer-md-audience {
    /* For "Influencer & Audience" text */
  }
  .header-col-influencer-lg {
    /* For "Influencer" text */
  }
  .header-col-audience-demographics {
    /* For "Audience Demographics" text */
  }
  .header-col-ai-score {
    /* For "AI Score" text */
  }
  .header-col-ai-reasoning {
    /* For "AI Reasoning" text */
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(300px, 1fr);
    gap: ${({ theme }) => theme.spacing[3]};
    padding: ${({ theme }) => theme.spacing[2]}
      ${({ theme }) => theme.spacing[4]};
    background-color: ${({ theme }) => theme.colors.background};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.text};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    box-shadow: ${({ theme }) => theme.boxShadow.sm};
    align-items: center;

    .header-col-influencer-md-audience {
      display: flex;
      grid-column: 1;
    }
    .header-col-influencer-lg {
      display: none;
    }
    .header-col-audience-demographics {
      display: none;
    }
    .header-col-ai-score {
      display: flex;
      grid-column: 2;
    }
    .header-col-ai-reasoning {
      display: none;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: minmax(220px, 0.75fr) 1fr 95px minmax(300px, 1.25fr);

    .header-col-influencer-md-audience {
      display: none;
    }
    .header-col-influencer-lg {
      display: flex;
      grid-column: 1;
    }
    .header-col-audience-demographics {
      display: flex;
      grid-column: 2;
    }
    .header-col-ai-score {
      display: flex;
      grid-column: 3;
    }
    .header-col-ai-reasoning {
      display: flex;
      grid-column: 4;
    }
  }
`

const HeaderColumn = styled.div<{ align?: "left" | "center" | "right" }>`
  display: flex;
  align-items: center;
  justify-content: ${({ align }) =>
    align === "right"
      ? "flex-end"
      : align === "center"
      ? "center"
      : "flex-start"};
  padding: ${({ theme }) => theme.spacing[2]};
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  box-shadow: ${({ theme }) => theme.boxShadow.sm};

  &:hover {
    background: #3d5bdb; /* Slightly darker shade of primary */
    box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    background: #3751d1; /* Even darker on active */
    box-shadow: 0 2px 4px rgba(67, 97, 238, 0.2);
  }
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EmailSection = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  margin: ${({ theme }) => theme.spacing[8]} 0;
  text-align: center;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  flex-direction: column;
`

const SpinnerIcon = styled.div`
  border: 4px solid ${({ theme }) => theme.colors.backgroundAlt};
  border-top: 4px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const LoadingText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.textLight};
`

const CTASection = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing[10]};
  padding: ${({ theme }) => theme.spacing[6]};
  background-color: ${({ theme }) => theme.colors.backgroundDark};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);

  /* Add subtle gradient overlay */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(61, 139, 240, 0.15) 0%,
      rgba(44, 125, 226, 0.1) 100%
    );
    z-index: 0;
    pointer-events: none;
  }

  /* Add subtle pattern overlay */
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      circle at 25% 25%,
      rgba(255, 255, 255, 0.05) 1%,
      transparent 6%
    );
    background-size: 50px 50px;
    z-index: 0;
    pointer-events: none;
  }
`

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
`

const CTABadge = styled.span`
  display: inline-block;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  text-transform: uppercase;
  letter-spacing: 1px;
`

const CTATitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  color: white;
  line-height: 1.3;
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes["3xl"]};
    text-align: center;
  }
`

const CTADescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
    text-align: center;
  }
`

const CTAFeatureList = styled.ul`
  text-align: left;
  margin: ${({ theme }) => theme.spacing[6]} auto;
  padding-left: 0;
  padding-right: 0;
  max-width: 900px;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
    column-gap: ${({ theme }) => theme.spacing[6]};
  }

  /* Add flex alignment to ensure vertical centering */
  align-items: center;
`

// Renamed from CTAFeatureItem and modified for flex gap
const StyledFeatureLi = styled.li`
  margin-bottom: 0;
  list-style-type: none;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  min-height: 36px;
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizes.md};
  gap: ${({ theme }) => theme.spacing[3]}; // Use gap for spacing icon and text
  // padding-left is removed, gap handles spacing
`

interface CTAFeatureItemProps {
  iconSrc: string
  children: React.ReactNode
}

// New React component for the feature item
const CTAFeatureItem: React.FC<CTAFeatureItemProps> = ({
  iconSrc,
  children,
}) => {
  return (
    <StyledFeatureLi>
      <img
        src={iconSrc}
        alt="" // Alt text is empty as icons are decorative alongside descriptive text
        style={{
          width: "30px",
          height: "30px",
          filter: "brightness(0) invert(1)",
          opacity: 0.9,
        }}
      />
      <span>{children}</span>
    </StyledFeatureLi>
  )
}

const FilterTagsWrapper = styled.div`
  display: flex;
  flex-direction: column; // Stack items by default for mobile
  align-items: stretch; // Stretch items (like the button) to full width by default
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.boxShadow.sm};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    // The existing gap will apply between items in a row.
    // If a different horizontal gap is needed, it can be specified here.
  }
`

const FilterTag = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`

const UnderlineCTALink = styled.a`
  display: inline-block;
  position: relative;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  cursor: pointer;
  padding-bottom: 2px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 1px;
    bottom: 0;
    left: 0;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.3s ease-in-out;
  }

  &:hover::after {
    width: 100%;
  }
`

// Helper function to get readable names for filter values
const getReadableFilterName = (key: string, value: string): string => {
  switch (key) {
    case "industry":
      switch (value) {
        case "beauty-cosmetics":
          return "Beauty & Cosmetics"
        case "consumer-electronics":
          return "Consumer Electronics"
        case "food-beverage":
          return "Food & Beverage"
        default:
          return value
      }
    case "country":
      switch (value) {
        case "uk":
          return "United Kingdom"
        case "germany":
          return "Germany"
        case "italy":
          return "Italy"
        case "united-states":
          return "United States"
        default:
          return value
      }
    case "budget":
      switch (value) {
        case "0-3000":
          return "Up to €2,999"
        case "3000-5000":
          return "€3,000 - €4,999"
        case "5000-10000":
          return "€5,000 - €9,999"
        case "10000+":
          return "€10,000 and above"
        default:
          return value
      }
    default:
      return value
  }
}

const InfluencerList: React.FC<InfluencerListProps> = ({
  influencers,
  isLoading,
  hasSubmittedEmail,
  onEmailSubmit,
  currentFilters,
  onChangeFilters,
  signupUrl,
  onSelectionChange,
  showCTA = true,
}) => {
  const [additionalInfluencersCount] = useState(() => {
    const LSTORAGE_KEY = "additionalInfluencersCount"
    let storedCount = localStorage.getItem(LSTORAGE_KEY)
    if (storedCount) {
      const parsedCount = parseInt(storedCount, 10)
      // Validate if the stored number is within our expected range
      if (parsedCount >= 17 && parsedCount <= 34) {
        return parsedCount
      }
    }
    // If not found or not valid, generate, store, and return
    const newCount = Math.floor(Math.random() * (34 - 17 + 1)) + 17
    localStorage.setItem(LSTORAGE_KEY, newCount.toString())
    return newCount
  })

  // Ensure we have a default signup URL if none is provided and handle email parameters
  const getSignupUrl = (addEmail = false) => {
    const baseUrl = signupUrl || "https://business.hypefy.ai/auth/signup"

    // If we don't need to add email or there's no email, just return the URL as is
    if (!addEmail || !currentFilters.businessEmail) return baseUrl

    // Check if the URL already has parameters
    const hasParams = baseUrl.includes("?")
    // Check if the URL already has the email parameter
    const hasEmailParam = baseUrl.includes("email=")

    // If the URL already has the email parameter, don't add it again
    if (hasEmailParam) return baseUrl

    // Add the email parameter with the correct separator
    return `${baseUrl}${hasParams ? "&" : "?"}email=${encodeURIComponent(
      currentFilters.businessEmail
    )}`
  }

  // Construct headline based on filters
  const getHeadline = () => {
    const industry = getReadableFilterName("industry", currentFilters.industry)
    const country = getReadableFilterName("country", currentFilters.country)

    return `Top ${country} ${industry} Creators`
  }

  // Get the budget range from the current filters - keep this for filtering logic
  const getBudgetRange = (): { min: number; max: number } => {
    // Map budget strings to min/max values
    const budgetMap: Record<string, { min: number; max: number }> = {
      "0-3000": { min: 0, max: 2999 },
      "0-4999": { min: 0, max: 4999 },
      "3000-5000": { min: 3000, max: 4999 },
      "5000-10000": { min: 5000, max: 9999 },
      "10000+": { min: 10000, max: Number.MAX_SAFE_INTEGER }, // Using MAX_SAFE_INTEGER for a large upper bound

      // Legacy formats for backward compatibility
      "5000-15000": { min: 5000, max: 14999 },
      "15000-25000": { min: 15000, max: 24999 },
      "25000-50000": { min: 25000, max: 49999 },
      "50000+": { min: 50000, max: Number.MAX_SAFE_INTEGER }, // Using MAX_SAFE_INTEGER
    }

    return budgetMap[currentFilters.budget] || { min: 0, max: 2999 } // Default range, adjusted
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        <SpinnerIcon />
        <LoadingText>Finding the best influencers for you...</LoadingText>
      </LoadingContainer>
    )
  }

  if (influencers.length === 0) {
    // Check if budget filter might be the reason
    const budgetRange = getBudgetRange()
    const isBudgetLow = budgetRange.max <= 3000

    return (
      <ListContainer>
        <Title>No influencers found</Title>
        {isBudgetLow ? (
          <Subtitle>
            Your budget of{" "}
            {getReadableFilterName("budget", currentFilters.budget)} may be too
            restrictive. Consider increasing your budget or contact us for a
            custom solution.
          </Subtitle>
        ) : (
          <Subtitle>
            Please try changing your filters or contact us for a custom
            solution.
          </Subtitle>
        )}
        <Button onClick={onChangeFilters} style={{ marginRight: "10px" }}>
          New Search
        </Button>
        <a
          href={getSignupUrl(true)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            padding: "8px 16px",
            fontWeight: "bold",
            fontSize: "14px",
            borderRadius: "8px",
            backgroundColor: "#fff",
            color: "#4361EE",
            border: "1px solid #4361EE",
            textAlign: "center",
            textDecoration: "none",
            cursor: "pointer",
            marginLeft: "10px",
            transition: "0.15s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(67, 97, 238, 0.05)"
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(67, 97, 238, 0.1)"
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#fff"
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "none"
          }}
        >
          Get Custom Matches
        </a>
      </ListContainer>
    )
  }

  return (
    <ListContainer>
      {<Title>{getHeadline()}</Title>}
      <Subtitle>
        This list features {influencers.length} top-performing influencers for
        targeting audiences in{" "}
        {getReadableFilterName("country", currentFilters.country)} interested in{" "}
        {getReadableFilterName("industry", currentFilters.industry)}. Need more
        options? Sign up for free and get a <em>fully personalized list</em>{" "}
        matched to your exact product, audience, and campaign goals.{" "}
        <UnderlineCTALink
          href={getSignupUrl(true)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {"Start Your First Campaign Now "}
          &#8594;
        </UnderlineCTALink>
      </Subtitle>

      <FilterTagsWrapper>
        {currentFilters.industry && (
          <FilterTag>
            Industry:{" "}
            {getReadableFilterName("industry", currentFilters.industry)}
          </FilterTag>
        )}
        {currentFilters.country && (
          <FilterTag>
            Country: {getReadableFilterName("country", currentFilters.country)}
          </FilterTag>
        )}
        {currentFilters.budget && (
          <FilterTag>
            Budget: {getReadableFilterName("budget", currentFilters.budget)}
          </FilterTag>
        )}
        <Button variant="outlined-grey" onClick={onChangeFilters} size="small">
          New Discovery
        </Button>
      </FilterTagsWrapper>

      <ListHeader>
        <HeaderColumn className="header-col-influencer-md-audience">
          Influencer & Audience
        </HeaderColumn>
        <HeaderColumn className="header-col-influencer-lg">
          Influencer
        </HeaderColumn>
        <HeaderColumn className="header-col-audience-demographics">
          Audience Demographics
        </HeaderColumn>
        <HeaderColumn className="header-col-ai-score">AI Score</HeaderColumn>
        <HeaderColumn className="header-col-ai-reasoning">
          AI Reasoning
        </HeaderColumn>
      </ListHeader>

      <List>
        {influencers.map((influencer, index) => (
          <InfluencerRow
            key={influencer.id}
            isPreview={index > 2 && !hasSubmittedEmail}
          >
            <InfluencerCard
              influencer={influencer}
              isPreview={index > 2 && !hasSubmittedEmail}
              businessEmail={currentFilters.businessEmail}
              index={index}
              selectedCountryName={getReadableFilterName(
                "country",
                currentFilters.country
              )}
              selectedIndustryName={getReadableFilterName(
                "industry",
                currentFilters.industry
              )}
            />
          </InfluencerRow>
        ))}
      </List>

      {influencers.length > 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "12px 20px",
            margin: "24px auto",
            fontSize: "0.95em",
            color: "#4A5568",
            fontWeight: 500,
            backgroundColor: "#F7FAFC",
            borderRadius: "8px",
            maxWidth: "450px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <span style={{ fontSize: "1.1em", marginRight: "6px" }}>✨</span>
          <span style={{ fontWeight: "bold", color: "#2D3748" }}>
            + {additionalInfluencersCount}
          </span>{" "}
          more influencers matched to your needs
        </div>
      )}

      {/* CTA Section */}
      {showCTA && (
        <CTASection id="upgrade-cta">
          <CTAContent>
            <CTABadge>READY FOR VIP LEVEL?</CTABadge>
            <CTATitle>
              Want a List Tailored to Your Product — Not Just Your Industry?
            </CTATitle>
            <CTADescription>
              This preview was just the warm-up. Sign up now to unlock your{" "}
              <strong>custom influencer list</strong> — built around your{" "}
              <em>exact</em> product, ideal customer, and budget.
              <strong>
                {" "}
                No filters. No guesswork. Just a high-performing list —
                instantly.
              </strong>{" "}
              <br /> <br />
              And that's just the beginning. With Hypefy, you also get:
            </CTADescription>

            <CTAFeatureList>
              <CTAFeatureItem iconSrc="/icons/price-tag-round-svgrepo-com.svg">
                AI-powered pricing, brief, outreach and negotiation, fully
                automated
              </CTAFeatureItem>
              <CTAFeatureItem iconSrc="/icons/chat-round-dots-svgrepo-com.svg">
                Contracts handling, content approvals and creator communication
              </CTAFeatureItem>
              <CTAFeatureItem iconSrc="/icons/report-svgrepo-com.svg">
                Live reporting, ROI tracking and performance insights
              </CTAFeatureItem>
              <CTAFeatureItem iconSrc="/icons/table-remove-svgrepo-com.svg">
                Payment automation—no chasing, no spreadsheets
              </CTAFeatureItem>
            </CTAFeatureList>

            <a
              href={getSignupUrl(true)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                padding: "16px 24px",
                fontWeight: "bold",
                fontSize: "16px",
                borderRadius: "8px",
                backgroundColor: "#4361EE",
                color: "white",
                minWidth: "250px",
                textAlign: "center",
                textDecoration: "none",
                cursor: "pointer",
                border: "none",
                position: "relative",
                zIndex: 10,
                marginTop: "16px",
                transition: "0.15s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#3D5BDB"
                e.currentTarget.style.transform = "translateY(-2px)"
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(67, 97, 238, 0.3)"
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#4361EE"
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "none"
              }}
            >
              Sign Up For Free
            </a>
          </CTAContent>
        </CTASection>
      )}
    </ListContainer>
  )
}

export default InfluencerList
